import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import {
  FaIcon,
  faCalculator,
  faFileSolid,
  faCalendarArrowUpRegular,
  faBedRegular,
  faCoinsRegular,
  faMoneyBillRegular,
} from '@/lib/fa'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { TableHeaderColumn } from '@/modules/drivers/types'
import SummaryHeader from './SummaryHeader'

interface SummaryItem {
  name: string
  amount: number
  negative?: boolean
  icon?: IconProp
}

const SummaryColumns: TableHeaderColumn[] = [
  { title: 'POZYCJA', key: 'name', type: 'string', sort: false },
  { title: 'KWOTA', key: 'amount', type: 'number', sort: false },
]

const SummaryModal = ({ tripData, onClose }) => {
  const { t } = useLocalize()

  const summaryItems = React.useMemo<SummaryItem[]>(() => {
    const items: SummaryItem[] = []

    // Allowances total
    const allowancesTotal = (tripData?.allowances || []).reduce(
      (total: number, current: any) =>
        total + (current.amount || 0) * (current.exchangeRate || 1),
      0,
    )
    items.push({
      name: t('newDelegation.summary.allowances') || 'Diety',
      amount: allowancesTotal,
      icon: faCoinsRegular,
    })

    // Accommodations total
    const accommodationsTotal = (tripData?.accommodations || []).reduce(
      (total: number, current: any) => total + (current.plnAmount || 0),
      0,
    )
    items.push({
      name: t('newDelegation.summary.accomodation') || 'Noclegi',
      amount: accommodationsTotal,
      icon: faBedRegular,
    })

    // Invoices total
    const invoicesTotal = (tripData?.invoices || []).reduce(
      (total: number, current: any) => total + (current.plnAmount || 0),
      0,
    )
    items.push({
      name: t('newDelegation.summary.invoices') || 'Koszty',
      amount: invoicesTotal,
      icon: faMoneyBillRegular,
    })

    // Advance Payments total (negative)
    const advancePaymentsTotal = (tripData?.advancePayments || []).reduce(
      (total: number, current: any) => total + (current.plnAmount || 0),
      0,
    )
    items.push({
      name: t('newDelegation.summary.advance-payments') || 'Zaliczki',
      amount: advancePaymentsTotal,
      negative: true,
      icon: faCalendarArrowUpRegular,
    })

    // Total sum
    const totalSum = items.reduce(
      (total: number, current: SummaryItem) =>
        total + (current.negative ? -current.amount : current.amount),
      0,
    )
    items.push({
      name: t('newDelegation.summary.sum') || 'Razem',
      amount: totalSum,
      icon: faCalculator,
    })

    return items
  }, [tripData, t])

  const formatNumber = (value: number, isNegative?: boolean): string => {
    if (!value && value !== 0) return '-'
    const num = Math.abs(value)
    const formatted =
      num % 1 === 0 ? num.toString() : parseFloat(num.toFixed(2)).toString()
    return `${isNegative ? '-' : ''}${formatted} PLN`
  }
  return (
    <ModalLayout>
      <div className="tw-w-full tw-max-w-[650px] tw-rounded-lg tw-bg-white tw-pb-4">
        <SummaryHeader onClose={onClose} />

        <div className="tw-overflow-x-auto  tw-border-300 tw-border-t tw-flex tw-flex-col tw-gap-4   tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FaIcon icon={faFileSolid} className='tw-text-content-base/90'/>
            <p className="tw-text-sm tw-font-medium tw-text-content-base/90">
              Podstawa
            </p>
          </div>
          <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-base-300">
            <table className="tw-w-full tw-table-auto tw-border-collapse">
              <tbody>
              {summaryItems.map((item, index) => {
                const isLast = index === summaryItems.length - 1
                const isAdvancePayment = item.negative
                return (
                  <tr
                    key={index}
                    className={`hover:tw-bg-base-50 tw-rounded-lg tw-border-b tw-border-gray-300 ${
                      isLast ? 'tw-bg-base-300' : ''
                    }`}
                  >
                    <TableTextRow>
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {item.icon &&
                          (typeof item.icon === 'string' ? (
                            <img
                              src={item.icon}
                              alt=""
                              className="tw-h-5 tw-w-5"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : (
                            <FaIcon
                              icon={item.icon}
                              className="tw-h-5 tw-w-5"
                            />
                          ))}
                        <span
                          className={isLast ? 'tw-font-semibold' : ''}
                          style={isAdvancePayment ? { color: '#F00' } : {}}
                        >
                          {item.name}
                        </span>
                      </div>
                    </TableTextRow>
                    <TableTextRow>
                      <span
                        className={isLast ? 'tw-font-semibold' : ''}
                        style={isAdvancePayment ? { color: '#F00' } : {}}
                      >
                        {formatNumber(item.amount, item.negative)}
                      </span>
                    </TableTextRow>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default SummaryModal
